/**
 * useVariantItemsHelpers.js - Helper functions ONLY (no state management)
 *
 * This composable provides PURE FUNCTIONS for calculations and validations.
 * The component manages its own state with ref([]).
 */

import { ItemStatus } from '@/enums/commerce/ItemStatus.js';
import { ItemStatusCategories } from '@/enums/commerce/ItemStatusConfig.js';

/**
 * Helper functions that DON'T manage state
 * These are just utilities that components can use
 */
export function useVariantItemsHelpers(options = {}) {
  const {
    formatCurrency = (value) => `${(value / 100).toFixed(2)} ج.م`,
  } = options;

  /**
     * Calculate financial totals from items array
     * @param {Array} items - Array of items
     * @param {Number} variantPrice - Price per unit (in cents)
     * @returns {Object} - { totalQuantity, totalAmount, totalDeposit, remainingAmount }
     */
  const calculateTotals = (items, variantPrice) => {
    const activeItems = items.filter(item => item.status !== ItemStatus.CANCELLED);

    const totalQuantity = activeItems.reduce((sum, item) =>
      sum + (item.qty || 0), 0,
    );

    const totalAmount = activeItems.reduce((sum, item) =>
      sum + ((item.qty || 0) * variantPrice), 0,
    );

    const totalDeposit = activeItems.reduce((sum, item) =>
      sum + (item.depositAmount || 0), 0,
    );

    const remainingAmount = totalAmount - totalDeposit;

    return {
      totalQuantity,
      totalAmount,
      totalDeposit,
      remainingAmount,
    };
  };

  /**
     * Calculate full deposit for an item
     */
  const getFullDeposit = (item, variantPrice) => {
    return (item.qty || 0) * variantPrice;
  };

  /**
     * Validate if items can be saved
     * @returns {Object} - { canSave: boolean, errors: Array }
     */
  const validateItems = (items) => {
    const errors = [];

    // Must have at least one item with qty > 0
    const hasValidItems = items.some(item => item.qty > 0);
    if (!hasValidItems) {
      errors.push({ type: 'no_items', message: 'لازم تضيف منتج واحد على الأقل بكمية أكبر من صفر' });
    }

    // Check for items with qty=0 (excluding cancelled)
    items.forEach((item, index) => {
      if (item.qty === 0 && item.status !== ItemStatus.CANCELLED) {
        errors.push({
          type: 'zero_qty',
          index,
          message: 'مش ممكن تحفظ عنصر بكمية صفر',
        });
      }
    });

    // Check reserved names
    items.forEach((item, index) => {
      if (item.reservationStatus !== 'none') {
        const name = (item.reservedForName || '').trim();
        if (name.length < 5) {
          errors.push({
            type: 'invalid_reservation',
            index,
            field: 'reservedForName',
            message: 'لازم يكون اسم المحجوز له 5 أحرف على الأقل',
          });
        }
      }
    });

    return {
      canSave: errors.length === 0 && hasValidItems,
      errors,
    };
  };

  /**
     * Create a new item template
     */
  const createNewItem = () => ({
    tempId: `new-${Date.now()}-${Math.random()}`,
    existingId: null,
    qty: 1,
    notes: '',
    avatarUrl: '',
    reservationStatus: 'none',
    depositAmount: 0,
    reservedForName: '',
    status: ItemStatus.PENDING,
  });

  /**
     * Normalize item data (ensure all required fields exist)
     */
  const normalizeItem = (item) => ({
    ...item,
    reservationStatus: item.reservationStatus || 'none',
    depositAmount: item.depositAmount || 0,
    reservedForName: item.reservedForName || '',
    status: item.status || ItemStatus.PENDING,
  });

  /**
     * Check if item is editable
     */
  const isItemEditable = (item) => {
    if (!item.existingId) return true;
    return item.status === ItemStatus.PENDING || !item.status;
  };

  /**
     * Check if item is cancelled
     */
  const isItemCancelled = (item) => {
    return item.status === ItemStatus.CANCELLED;
  };

  /**
     * The rows a bulk save is allowed to POST.
     *
     * Two independent conditions:
     *
     *  1. `qty > 0` — a zero-qty row is not a save, and the editor surfaces
     *     `item.fields.cannotSaveZeroQty` rather than sending it.
     *
     *  2. AUTHORITY. An existing row may only be sent while the member still has
     *     the right to change it — QUANTITY_EDITABLE, i.e. PENDING. This used to
     *     be `qty > 0 || status === CANCELLED` with the caller stripping
     *     CANCELLED again afterwards, which meant EVERY other sibling of the
     *     variant went up with its id on every save: COLLECTING, COLLECTED,
     *     SHIPPED, OUT_OF_STOCK, WRITTEN_OFF. Editing one PENDING line re-posted
     *     the qty, notes, avatar and reservation of a line a collector was
     *     physically out shopping for, built from a local snapshot that may
     *     already be stale — the exact race the owner rule forbids
     *     (owner-locked 2026-08-09, no member edits during COLLECTING).
     *
     * A row with no `existingId` is a CREATE, not an update, so authority does
     * not apply — it is always eligible. (`createNewItem` stamps PENDING anyway,
     * and an unsaved row is spliced out on remove rather than flipped to
     * CANCELLED, so this can never smuggle a cancelled line through as a create.)
     *
     * The backend independently no-ops a re-sent locked row — see the comment in
     * OrderBoxService::processItemData, which was written BECAUSE the PWA posted
     * the full list. This filter means the PWA no longer needs that safety net:
     * the two now agree exactly, since the backend's condition is
     * `status !== PENDING` and QUANTITY_EDITABLE is [PENDING].
     *
     * Omitting a row does NOT delete it: syncProductItems collects
     * `$processedItemIds` but never prunes against it, and deletion happens only
     * on an explicit `qty === 0` update.
     *
     * `alsoEditable` is the ONE narrow exception, and the caller has to name it:
     * a «تدبيسة» line is born COLLECTED (the piece is already on the club's
     * shelf) yet the backend still takes quantity edits on it while its carton is
     * open (`OrderBoxService::resaleLineTakesEdits`). QUANTITY_EDITABLE stays
     * [PENDING] for everything else — only rows the predicate vouches for ride
     * along on top of it.
     *
     * @param {Array} items
     * @param {(item: object) => boolean} [alsoEditable]
     */
  const getItemsToSave = (items, alsoEditable = () => false) => {
    return items.filter(item =>
      item.qty > 0
      && (
        !item.existingId
        || ItemStatusCategories.QUANTITY_EDITABLE.includes(item.status)
        || alsoEditable(item)
      ),
    );
  };

  /**
     * Create snapshot for change detection
     */
  const createSnapshot = (items) => {
    return JSON.stringify(items);
  };

  /**
     * Compare snapshots
     */
  const hasChanges = (currentSnapshot, initialSnapshot) => {
    return currentSnapshot !== initialSnapshot;
  };

  return {
    // Calculations
    calculateTotals,
    getFullDeposit,

    // Validation
    validateItems,

    // Item helpers
    createNewItem,
    normalizeItem,
    isItemEditable,
    isItemCancelled,
    getItemsToSave,

    // Change detection
    createSnapshot,
    hasChanges,

    // Utilities
    formatCurrency,
  };
}
